import React, { useEffect, useState } from 'react';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellNumber, CellMuted } from '../../../ui/DataTable/cells';
import Button from '../../../ui/Button/Button';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { Input, Select } from '../../../ui/Field/Field';
import { finesService, errorMessage } from '../../../services/finesService';
import { Field, Pill } from './ui';
import { templateCount } from './templateCount';

/**
 * The Traffic Fines "Reports" tab (it was "Files"): the reports built from the fines database, and where the
 * engine reads its sources from. Nothing is uploaded here any more (owner, 28 Sep 2026): the police fines come from
 * the RTA portal, contracts and the E-Check log from yelo (the engine's hourly Ejar sync).
 */
export default function FilesTab() {
    const [linked, setLinked] = useState(null);
    const [days, setDays] = useState([]);          // every violation day in the database, newest first
    const [day, setDay] = useState('');            // quick pick of one violation day (sets the range below)
    const [from, setFrom] = useState('');          // violation date range; both empty = every fine
    const [to, setTo] = useState('');
    const [branches, setBranches] = useState([]);
    const [branchCounts, setBranchCounts] = useState({}); // branch -> fines in the chosen day (or overall)
    const [downloading, setDownloading] = useState('');
    const [error, setError] = useState(null);
    const [templateResult, setTemplateResult] = useState(null);   // {key, rows, skipped, other} of the last template download

    const load = () => {
        finesService.inputs().then((r) => { setLinked(r.linked || null); }).catch((e) => setError(errorMessage(e)));
        // Days come from the violation dates themselves (every fine ever recorded), not from the engine's run folders
        finesService.days('fine').then((r) => setDays(Array.isArray(r) ? r : (r.days || []))).catch(() => {});
        finesService.facets().then((f) => setBranches(f.branches || [])).catch(() => {});
    };
    useEffect(load, []);

    const pickDay = (d) => { setDay(d); setFrom(d); setTo(d); };
    const inRange = (d) => (!from || d >= from) && (!to || d <= to);
    const rangeParams = { ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) };
    const rangeLabel = !from && !to ? 'all' : from === to ? from : `${from || 'start'}_${to || 'today'}`;

    // How many fines each branch has in the chosen range (one tiny search per branch)
    useEffect(() => {
        let cancelled = false;
        const range = rangeParams;
        Promise.all(branches.map((b) => finesService.search({ ...range, branch: b, size: 1 }).then((r) => [b, r.total]).catch(() => [b, null])))
            .then((pairs) => { if (!cancelled) setBranchCounts(Object.fromEntries(pairs)); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, branches]);

    const tag = (b) => `${rangeLabel}${b ? `_${b.replace(/\s+/g, '_')}` : ''}`;
    const range = (b) => ({ ...rangeParams, ...(b ? { branch: b } : {}) });
    // Reports are built from the database for the chosen violation day (and branch): the company upload sheet and the fines summary
    const downloadTemplate = async (b = '') => {
        setError(null); setDownloading(`template:${b}`);
        try {
            const r = await finesService.downloadTemplate(range(b), `Traffic_Violations_${tag(b)}.xlsx`);
            setTemplateResult({ key: tag(b), ...r });
        }
        catch (e) { setError(errorMessage(e)); }
        finally { setDownloading(''); }
    };
    const downloadSummary = async (b = '') => {
        setError(null); setDownloading(`summary:${b}`);
        try { await finesService.downloadSummary(range(b), `Daily_Fines_Summary_${tag(b)}.xlsx`); }
        catch (e) { setError(errorMessage(e)); }
        finally { setDownloading(''); }
    };
    const inRangeDays = days.filter((d) => inRange(d.day));
    const selected = inRangeDays.length ? inRangeDays.reduce((acc, d) => ({
        count: acc.count + (d.count || 0), unpaid: acc.unpaid + (d.unpaid || 0), paid: acc.paid + (d.paid || 0),
        billed: acc.billed + Number(d.billed || d.amount || 0)
    }), { count: 0, unpaid: 0, paid: 0, billed: 0 }) : null;
    const totalCount = selected?.count || 0;
    // the template's own count once downloaded for this range (it leaves fines out), else the range count
    const template = templateCount(totalCount, templateResult?.key === tag('') ? templateResult : null);
    const badRange = from && to && from > to;

    const downloadIcon = <Icon glyph={ICONS.download} size="sm" />;

    return (
        <div className="space-y-6">
            {linked?.enabled && (
                <Card title="Linked to the yelo database" subtitle="Contracts and the E-Check log come from yelo (the engine's hourly Ejar sync); the police fines come from the RTA portal. Nothing is uploaded here.">
                    <div className="flex flex-wrap gap-2 text-sm">
                        <Pill tone="indigo">Business Bay contracts: {linked.cached_counts?.contracts ?? '…'}</Pill>
                        <Pill tone="indigo">Airport contracts: {linked.cached_counts?.airport_contracts ?? '…'}</Pill>
                        <Pill tone="indigo">E-Check events: {linked.echeck?.records ?? '…'}</Pill>
                        {linked.echeck?.last_sync && <Pill>E-Check synced {String(linked.echeck.last_sync).slice(0, 16).replace('T', ' ')}</Pill>}
                        {linked.error && <Pill tone="red">{linked.error}</Pill>}
                    </div>
                </Card>
            )}
            <div>
                <h3 className="ds-section-title mb-1">Reports</h3>
                {error && <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                <p className="text-sm text-gray-600 mb-3">Built from the database for a violation day or a range of days (leave both dates empty for every fine ever recorded). Each branch summary also carries its Traffic_Violations sheet, time in 24 h. The Traffic Violation Template leaves out fines already paid by the customer (they must not be billed again), fines with no contract and fines already on Ejar (Ejar refuses the whole file otherwise); company-paid and unpaid fines are in. Its row shows the fines in the range until it is downloaded, then the rows in the file.</p>
                <div className="flex flex-wrap items-end gap-3 mb-3">
                    <Field label="Violation day">
                        <Select value={day} onChange={(e) => pickDay(e.target.value)} title="Quick pick: one violation day" placeholder="Pick a day…"
                                options={days.map((d) => ({ value: d.day, label: `${d.day} (${d.count})` }))} />
                    </Field>
                    <Field label="From">
                        <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setDay(''); }} />
                    </Field>
                    <Field label="To">
                        <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setDay(''); }} />
                    </Field>
                    {(from || to) && <Button variant="ghost" onClick={() => { setFrom(''); setTo(''); setDay(''); }}>All days</Button>}
                    {badRange && <Pill tone="red">&quot;From&quot; is after &quot;To&quot;</Pill>}
                    {selected && !badRange && (
                        <span className="flex flex-wrap gap-1 text-sm self-center">
                            <Pill tone="amber">{selected.unpaid} unpaid</Pill>
                            <Pill tone="green">{selected.paid} paid</Pill>
                            <Pill>{Number(selected.billed || 0).toLocaleString()} AED</Pill>
                        </span>
                    )}
                </div>
                <TableContainer>
                    <Table>
                        <TableHead><TableRow><TableHeader>Report</TableHeader><TableHeader>Fines</TableHeader><TableHeader><span className="sr-only">Download</span></TableHeader></TableRow></TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>Daily Fines Summary <span className="text-gray-500">- all branches (Overview, All_Fines, Unpaid, Paid, Unassigned, By_Branch, Traffic_Violations)</span></TableCell>
                                <TableCell><CellNumber value={totalCount} /></TableCell>
                                <TableCell><Button variant="primary" size="sm" leadingIcon={downloadIcon} disabled={!!downloading || badRange} onClick={() => downloadSummary('')}>{downloading === 'summary:' ? 'Preparing…' : 'Download'}</Button></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Traffic Violation Template <span className="text-gray-500">- all branches, the company upload sheet</span></TableCell>
                                <TableCell><CellNumber value={template.value} /><CellMuted>{template.note}</CellMuted></TableCell>
                                <TableCell><Button size="sm" leadingIcon={downloadIcon} disabled={!!downloading || badRange} onClick={() => downloadTemplate('')}>{downloading === 'template:' ? 'Preparing…' : 'Download'}</Button></TableCell>
                            </TableRow>
                            {branches.map((b) => (
                                <TableRow key={b}>
                                    <TableCell>Fines Summary <span className="text-gray-500">- {b}</span></TableCell>
                                    <TableCell><CellNumber value={branchCounts[b] === undefined ? '…' : branchCounts[b] === null ? '?' : branchCounts[b]} /></TableCell>
                                    <TableCell><Button size="sm" leadingIcon={downloadIcon} disabled={!!downloading || badRange || branchCounts[b] === 0} onClick={() => downloadSummary(b)}>{downloading === `summary:${b}` ? 'Preparing…' : 'Download'}</Button></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}
